import React, { useState } from 'react';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { FrameHeaderHeight } from 'styles/constants';
import { ExpandIcon, MeasureAxisIcon, MouseIcon } from 'icons';
import { OverflowTooltip } from 'components';
import { themes } from 'styles/themes/wikifactoryNew';
import {
  NavCubeExpandStyled,
  NodeTitle,
  IconStyled,
  ActionNodeStyled,
} from './navCube.styled';

type NavCubeProps = {
  mobileMode?: boolean;
};

const ModeWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className=' ml-1'>{children}</div>;
};

type OptionItem = {
  key?: React.Key;
  selected?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  stacked: number;
  label: React.ReactNode;
};

type ModeCollapseProps = {
  items: OptionItem[];
  title: React.ReactNode;
  icon: React.ReactNode;
};

const OperateRenderer = ({ items }: { items: OptionItem[] }) => {
  return (
    <ModeWrapper>
      {items.map(({ label, key, stacked, ...rest }, index) => (
        <ActionNodeStyled stacked={stacked ?? 2} key={key ?? index} {...rest}>
          {label}
        </ActionNodeStyled>
      ))}
    </ModeWrapper>
  );
};

const ModeCollapse = ({ title, icon, items }: ModeCollapseProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className=' text-xs'>
      <NodeTitle>
        <div className='flex w-full flex-nowrap items-center justify-between'>
          <div className='flex items-center'>
            {icon}
            {title}
          </div>
          <ExpandIcon
            width={16}
            height={12}
            onClick={() => setIsExpanded(!isExpanded)}
            className={`${
              isExpanded ? '-rotate-90' : 'rotate-90'
            } cursor-pointer`}
          />
        </div>
      </NodeTitle>
      {isExpanded && <OperateRenderer items={items} />}
    </div>
  );
};

export const useNavCube = (props: NavCubeProps = {}) => {
  const viewerControl = useViewerControl();

  const viewer = viewerControl?.viewer;
  const { settings, engine, navCube, isDiffing } = viewer || {};
  const isOrbitFixedUp = settings?.isFixedUp;
  const orbitEnum = viewer?.module.CameraOrbit || {};
  const orbitIx = settings?.GetOrbit();
  const camera = settings?.CurrentCamera();

  let stdLabelHigh = false;
  let stdLabel = settings?.stdViewLabel;
  if (!stdLabel || stdLabel === '') {
    stdLabel = navCube?.GetHighlightedStdViewLabel();
    stdLabelHigh = true;
  }

  let isFacetedBody = false;
  if (camera?.IsInstance()) {
    const instance = camera?.GetInstance();
    const brep = instance?.GetBody()?.GetBrep();
    if (!brep) {
      const meshTopo = instance?.GetBody()?.GetBodyMesh()?.GetMeshFrame();
      if (meshTopo?.isNull) isFacetedBody = true;
    }
  }

  const { assembly } = viewerControl.cadFile || {};
  let itemId = camera?.GetItem()?.uuid;
  if (!itemId) itemId = assembly?.GetRoot()?.uuid || '';

  const cameraOptions = camera?.Is3D() && !camera.IsPlaneXY();
  const upAxisOptions = camera?.Is3D() && !camera.IsPlaneXY();
  const renderOptions = cameraOptions && (isFacetedBody || !isDiffing);

  // up axis
  function toggleUpAxis() {
    settings.ToggleUp();
  }

  // mouse mode
  function toggleCameraMode(isOrbit: boolean) {
    viewerControl.viewer.settings.isLeftClickOrbit = isOrbit;
  }

  function togglePanMode() {
    if (props.mobileMode && !viewerControl.viewer.settings.isLeftClickOrbit) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      toggleOrbitMode();
      return;
    }

    toggleCameraMode(false);
  }

  function toggleOrbitMode() {
    if (props.mobileMode && viewerControl.viewer.settings.isLeftClickOrbit) {
      // eslint-disable-next-line no-use-before-define
      togglePanMode();
      return;
    }

    toggleCameraMode(true);
  }

  // camera orbit mode
  function setCameraOrbit(cameraEnum: any) {
    viewerControl.SetCameraOrbit(cameraEnum);
  }

  // orbit aux axes
  function toggleAuxOrbit() {
    if (settings) {
      settings.renderOrbitAux = !settings?.renderOrbitAux;
    }
  }

  // stcicky std view
  const toggleStickyStdView = (autoStickToSV: boolean) => {
    if (settings) {
      settings.autoStickToSV = autoStickToSV;
    }
  };

  // "standard" orthogonal views
  function setOrthoView(newOrbitEnum: any) {
    viewerControl.SetOrthoView(newOrbitEnum);
  }

  function highlightNavcube(newOrbitEnum: any) {
    const enumValue = newOrbitEnum ? newOrbitEnum.value : -1;
    // eslint-disable-next-line no-underscore-dangle
    viewerControl.viewer._wasm.SyncNavCubeHighlight(enumValue);
  }

  // solid boundary mode
  function updateSolidBoundary(boudnaryIx: any) {
    switch (boudnaryIx) {
      case 1: // solid (faces & edges)
        engine.SetSolidView();
        break;
      case 2: // wireframe
        if (!engine.IsWireframeView()) {
          engine.SetWireframeView();
        } else {
          engine.SetSolidView();
        }
        break;
      case 3: // edges
        if (!engine.IsEdgeView()) {
          engine.SetEdgesView();
        } else {
          engine.SetSolidView();
        }
        break;
      case 4: // faces
        if (!engine.IsFaceView()) {
          engine.SetFacesView();
        } else {
          engine.SetSolidView();
        }
        break;
      default:
        // solid (faces & edges)
        engine.SetSolidView();
        break;
    }
  }

  // color plots
  const currTessFieldType = camera?.GetCurrTessField();
  const fieldModeEnum = viewer?.module.TessFieldType;

  const plotTessField = (type: any) => {
    if (camera) {
      // switch to plot
      camera?.SetCurrTessField(type);
      // froce viewer render
      viewerControl?.cadFile?.assembly?.TriggerStateChange();
    }
  };

  if (!viewerControl?.viewerVisible) return null;

  const StickyStandardViewsOptions = [
    {
      label: 'When orbiting',
      selected: settings?.autoStickToSV,
      stacked: 1,
      onClick: () => toggleStickyStdView(true),
    },
    {
      label: 'Disabled',
      stacked: 1,
      selected: !settings?.autoStickToSV,
      onClick: () => toggleStickyStdView(false),
    },
  ];

  const maxSize = 7;
  const TessFieldOptions = [
    {
      visible: camera?.HasTessField(fieldModeEnum.TessFieldSharpEdges),
      selected: currTessFieldType === fieldModeEnum.TessFieldSharpEdges,
      onClick: () => plotTessField(fieldModeEnum.TessFieldSharpEdges),
      key: 'Edge Sharpness',
      label: <OverflowTooltip text='Edge Sharpness' maxSize={maxSize} />,
    },
    {
      visible: camera?.HasTessField(fieldModeEnum.TessFieldThickness),
      selected: currTessFieldType === fieldModeEnum.TessFieldThickness,
      onClick: () => plotTessField(fieldModeEnum.TessFieldThickness),
      label: <OverflowTooltip text='Thickness' maxSize={maxSize} />,
      key: 'Thickness',
    },
    {
      visible: camera?.HasTessField(fieldModeEnum.TessFieldOverHang),
      selected: currTessFieldType === fieldModeEnum.TessFieldOverHang,
      onClick: () => plotTessField(fieldModeEnum.TessFieldOverHang),
      key: 'Overhang',
      label: <OverflowTooltip text='Overhang' maxSize={maxSize} />,
    },
  ].filter(item => item.visible);

  const ColorPLotsOptions = [
    {
      selected: currTessFieldType === fieldModeEnum.TessFieldNone,
      onClick: () => plotTessField(fieldModeEnum.TessFieldNone),
      label: 'None',
    },
    ...TessFieldOptions,
  ];

  const StandardViewOptions = [
    {
      selected: orbitIx === orbitEnum.OrthoTop,
      onClick: () => setOrthoView(orbitEnum.OrthoTop),
      onMouseEnter: () => highlightNavcube(orbitEnum.OrthoTop),
      onMouseLeave: () => highlightNavcube(orbitEnum.Free),
      label: 'Top',
    },
    {
      selected: orbitIx === orbitEnum.OrthoBottom,
      onClick: () => setOrthoView(orbitEnum.OrthoBottom),
      onMouseEnter: () => highlightNavcube(orbitEnum.OrthoBottom),
      onMouseLeave: () => highlightNavcube(orbitEnum.Free),
      label: 'Bottom',
    },
    {
      selected: orbitIx === orbitEnum.OrthoFront,
      onClick: () => setOrthoView(orbitEnum.OrthoFront),
      onMouseEnter: () => highlightNavcube(orbitEnum.OrthoFront),
      onMouseLeave: () => highlightNavcube(orbitEnum.Free),
      label: 'Front',
    },
    {
      selected: orbitIx === orbitEnum.OrthoRear,
      onClick: () => setOrthoView(orbitEnum.OrthoRear),
      onMouseEnter: () => highlightNavcube(orbitEnum.OrthoRear),
      onMouseLeave: () => highlightNavcube(orbitEnum.Free),
      label: 'Rear',
    },
    {
      selected: orbitIx === orbitEnum.OrthoLeft,
      onClick: () => setOrthoView(orbitEnum.OrthoLeft),
      onMouseEnter: () => highlightNavcube(orbitEnum.OrthoLeft),
      onMouseLeave: () => highlightNavcube(orbitEnum.Free),
      label: 'Left',
    },
    {
      selected: orbitIx === orbitEnum.OrthoRight,
      onClick: () => setOrthoView(orbitEnum.OrthoRight),
      onMouseEnter: () => highlightNavcube(orbitEnum.OrthoRight),
      onMouseLeave: () => highlightNavcube(orbitEnum.Free),
      label: 'Right',
    },
  ];

  const RenderModeOptions = [
    {
      visible: true,
      selected: !engine?.GetMeshVisible() && engine.IsSolidView(),
      onClick: () => {
        updateSolidBoundary(1);
        engine.SetMeshVisible(false);
      },
      label: 'Default',
    },
    {
      selected: engine.IsFaceView(),
      onClick: () => {
        updateSolidBoundary(4);
        engine.SetMeshVisible(false);
      },
      visible: !isFacetedBody,
      label: 'Faces',
    },
    {
      selected: engine.IsWireframeView(),
      onClick: () => {
        updateSolidBoundary(2);
        engine.SetMeshVisible(false);
      },
      visible: !isFacetedBody,
      label: 'Frame',
    },
    {
      selected: engine.IsEdgeView(),
      onClick: () => {
        updateSolidBoundary(3);
        engine.SetMeshVisible(false);
      },

      visible: !isFacetedBody,
      label: 'Edges',
    },
    {
      selected: engine.GetMeshVisible() && engine.IsSolidView(),
      onClick: () => {
        engine.SetMeshVisible(true);
        updateSolidBoundary(1);
      },
      visible: camera?.IsInstance(),
      label: 'Mesh',
    },
  ].filter(item => item.visible);

  const MouseModeOptions = [
    {
      selected: settings?.isLeftClickOrbit,
      onClick: toggleOrbitMode,
      label: 'Orbit',
    },
    {
      label: 'Pan',
      selected: !settings?.isLeftClickOrbit,
      onClick: togglePanMode,
    },
  ];

  const OrbitAxesOptions = [
    {
      selected: settings?.renderOrbitAux,
      onClick: toggleAuxOrbit,
      label: 'On',
    },
    {
      label: 'Off',
      selected: !settings?.renderOrbitAux,
      onClick: toggleAuxOrbit,
    },
  ];

  const OrbitModeOptions = [
    {
      selected: isOrbitFixedUp,
      onClick: () => setCameraOrbit(orbitEnum.FixedUp),
      label: 'Constrained',
    },
    {
      label: <div className='ml-1 inline-flex'>Free</div>,
      selected: !isOrbitFixedUp,
      onClick: () => setCameraOrbit(orbitEnum.Free),
    },
  ];

  const renderMap: Record<string, any> = {
    camera: (
      <NavCubeExpandStyled top={FrameHeaderHeight * 3}>
        {/* Stick to Std.View */}

        <ModeCollapse
          title='Sticky Views'
          icon={<IconStyled name='render' size='default' viewBox='0 0 40 40' />}
          items={StickyStandardViewsOptions}
        />

        <ModeCollapse
          title='Standard Views'
          icon={
            <IconStyled
              name='standard_view'
              size='default'
              viewBox='0 0 40 40'
            />
          }
          items={StandardViewOptions}
        />

        {/* Gradient Plots */}
        {TessFieldOptions.length > 0 && (
          <ModeCollapse
            title='Color Plots'
            icon={
              <IconStyled
                name='collection'
                size='default'
                viewBox='0 0 24 24'
              />
            }
            items={ColorPLotsOptions}
          />
        )}
      </NavCubeExpandStyled>
    ),
    render: renderOptions && (
      <NavCubeExpandStyled>
        {/* Solid Boundary */}

        <ModeCollapse
          title='Render Mode'
          icon={<IconStyled name='render' size='default' viewBox='0 0 40 40' />}
          items={RenderModeOptions}
        />
      </NavCubeExpandStyled>
    ),
    orbit: cameraOptions && (
      <NavCubeExpandStyled>
        {/* Mouse mode */}

        <ModeCollapse
          title='Mouse Mode'
          icon={
            <MouseIcon
              className='mr-1'
              width={20}
              height={20}
              stroke={themes.color.wfBlack}
            />
          }
          items={MouseModeOptions}
        />

        {/* Orbit aux axes */}
        <ModeCollapse
          title={<p className='ml-1'>Orbit Axes</p>}
          icon={<MeasureAxisIcon width={20} className='' />}
          items={OrbitAxesOptions}
        />

        {/* orbit mode */}
        <ModeCollapse
          title='Orbit Mode'
          icon={
            <IconStyled name='orbit_free' size='default' viewBox='0 0 40 40' />
          }
          items={OrbitModeOptions}
        />
      </NavCubeExpandStyled>
    ),
  };

  return {
    renderMap,
    viewerControl,
    toggleUpAxis,
    toggleCameraMode,
    toggleOrbitMode,
    togglePanMode,
    setCameraOrbit,
    toggleAuxOrbit,
    toggleStickyStdView,
    setOrthoView,
    highlightNavcube,
    updateSolidBoundary,
    plotTessField,
    stdLabel,
    stdLabelHigh,
    cameraOptions,
    upAxisOptions,
    renderOptions,
  };
};
